import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { UpdateRecord } from '../models/update-record.model';
import { CreateUpdateRecordDto } from '../dtos/update-record.dto';
import { AuthService } from './auth.service';
import { StateSearchDTO, UpdateRecordSearchDTO } from '../models/search-filter.model';
import { ShiftClosure } from '../models/shift-closure.model';

@Injectable({
  providedIn: 'root',
})
export class UpdateRecordService {
  private apiUrl = environment.apiUrl + environment.updateRecord;

  constructor(private http: HttpClient, private auth: AuthService) {}

  /**
   * Normaliza la marca de cierre según el rol del usuario conectado.
   * Sobrescribe el campo estándar isMarkedForClosure con el valor específico del rol (DCS/Férrico) si existe.
   */
  private normalizeClosureMark(records: UpdateRecord[]): UpdateRecord[] {
    const isDcs = this.auth.hasRole('Novedades_Operador_DCS') || this.auth.hasRole('DCS') || this.auth.hasRole('dcs');
  const isFerrico = this.auth.hasRole('Novedades_Operador_Ferrico') || this.auth.hasRole('Novedades_Salmuera_I') || this.auth.hasRole('Novedades_Salmuera_II') || this.auth.hasRole('Novedades_Compresion') || this.auth.hasRole('Novedades_Electrolizadores') || this.auth.hasRole('Novedades_Subestacion') || this.auth.hasRole('Novedades_Movimiento_Producto') || this.auth.hasRole('FERRICO') || this.auth.hasRole('ferrico');
    const pick = (rec: any, keys: string[], fallback: any) => {
      for (const k of keys) {
        if (rec[k] !== undefined) return rec[k];
      }
      return fallback;
    };
    return (records || []).map((rec: any) => {
      const fallback = rec.isMarkedForClosure;
      // Mapeo solo por rol del usuario actual; sin fallback por área
      const value = isDcs
        ? pick(rec, ['isMarkedForClosureDcs', 'isMarkedForClosureDCS'], fallback)
        : isFerrico
          ? pick(rec, ['isMarkedForClosureFerrico'], fallback)
          : fallback; // jefe turno / novedades usa el estándar
      // Sobrescribe para que las vistas sigan usando rec.isMarkedForClosure
      return { ...rec, isMarkedForClosure: value } as UpdateRecord;
    });
  }

  getUpdateRecordByTypeAndArea(type: string, area: string): Observable<UpdateRecord[]> {
    const userId = this.auth.getUserId();
    return this.http
      .get<UpdateRecord[]>(`${this.apiUrl}/type/${encodeURIComponent(type)}/area/${encodeURIComponent(area)}/user/${userId}`)
      .pipe(map(list => this.normalizeClosureMark(list)));
  }

  getUpdateRecordsGeneral(  
    isClosed?: boolean,
    isMarkedForPreClosure?: boolean,
    searchUserId?: number,
   ): Observable<UpdateRecord[]> { 
    let params = new HttpParams();
    const updateAreas: string = 'general'
    params = params.set('updateAreas', updateAreas);
    
    if (isClosed !== undefined) params = params.set('isClosed', isClosed.toString());
    if (isMarkedForPreClosure !== undefined) params = params.set('isMarkedForPreClosure', isMarkedForPreClosure.toString());
    if (searchUserId !== undefined) params = params.set('searchUserId', searchUserId.toString());

    return this.http.get<UpdateRecord[]>(`${this.apiUrl}/areas`, { params });

    //https://cde-oxy.g-talent.cl/api/updateRecord/areas?updateAreas=general&isMarkedForPreClosure=false
  }


  getUpdateRecordsByAreas(
    areas: string[],
    isClosed?: boolean,
    isMarkedForClosure?: boolean,
    isMarkedForPreClosure?: boolean,
    forceUserScope: boolean = false,
    overrideSearchUserId?: number
  ): Observable<UpdateRecord[]> {
    const areaString = areas.join(',');
    const params: { [key: string]: string } = { updateAreas: areaString };
    
    if (isClosed !== undefined) {
      params['isClosed'] = isClosed.toString();
    }

    if (isMarkedForClosure !== undefined) {
      params['isMarkedForClosure'] = isMarkedForClosure.toString();
    }

    if (isMarkedForPreClosure !== undefined) {
      const isDcs = this.auth.hasRole('Novedades_Operador_DCS') || this.auth.hasRole('DCS') || this.auth.hasRole('dcs');
  const isFerrico = this.auth.hasRole('Novedades_Operador_Ferrico') || this.auth.hasRole('Novedades_Salmuera_I') || this.auth.hasRole('Novedades_Salmuera_II') || this.auth.hasRole('Novedades_Compresion') || this.auth.hasRole('Novedades_Electrolizadores') || this.auth.hasRole('Novedades_Subestacion') || this.auth.hasRole('Novedades_Movimiento_Producto') || this.auth.hasRole('FERRICO') || this.auth.hasRole('ferrico');
      const preParam = isDcs
        ? 'isMarkedForPreClosureDcs'
        : isFerrico
          ? 'isMarkedForPreClosureFerrico'
          : 'isMarkedForPreClosure';
      params[preParam] = isMarkedForPreClosure.toString();
    }
  
  const isFullNovedades = this.auth.hasRole('novedades');
      const userId = this.auth.getUserId();

      // Si se especifica overrideSearchUserId siempre usar endpoint sin /user y ese searchUserId
      if (overrideSearchUserId !== undefined) {
        params['searchUserId'] = String(overrideSearchUserId);
        return this.http.get<UpdateRecord[]>(`${this.apiUrl}/areas`, { params })
          .pipe(map(list => this.normalizeClosureMark(list)));
      }

      // Si se fuerza el scope por usuario o no es 'novedades', usar endpoint scopiado
      if (forceUserScope || !isFullNovedades) {
        return this.http.get<UpdateRecord[]>(`${this.apiUrl}/areas/user/${userId}`, { params })
          .pipe(map(list => this.normalizeClosureMark(list)));
      }
      // 'novedades' sin force: acceso completo; NO aplicar searchUserId para jefe de turno
      return this.http.get<UpdateRecord[]>(`${this.apiUrl}/areas`, { params })
        .pipe(map(list => this.normalizeClosureMark(list)));
  }

  getUpdateRecordsByTypeAndAreasDCS(type: string): Observable<UpdateRecord[]> {
    //ejemplo: https://cde-oxy.g-talent.cl/api/updateRecord/type/personal/areas?updateAreas=general,cloruro_ferrico,salmuera_i,salmuera_ii,compresion,electrolizadores,subestacion,movimiento_producto,dcs&isMarkedForPreClosure=false

    return this.http.get<UpdateRecord[]>(`${this.apiUrl}/type/${type}/areas?updateAreas=general,cloruro_ferrico,salmuera_i,salmuera_ii,compresion,electrolizadores,subestacion,movimiento_producto,dcs&isMarkedForPreClosure=false`);
    return of([])
  }

  getUpdateRecordsByTypeAndAreas__bySections( type: string, areas: string[]): Observable<UpdateRecord[]> {
    const areaString = areas.join(',');
    const params: { [key: string]: string } = { updateAreas: areaString };
    params['isClosed'] = false.toString();
    params['isMarkedForPreClosure'] = false.toString();
    return this.http.get<UpdateRecord[]>(`${this.apiUrl}/type/${type}/areas`, { params })
 
  }

  getUpdateRecordsPost(searchDTO: UpdateRecordSearchDTO): Observable<UpdateRecord[]> {
    const url = `${this.apiUrl}/search`; 
    return this.http.post<UpdateRecord[]>(url, searchDTO);
  }

  getUpdateRecordsByTypeAndAreas__bySections__dcs( type: string, areas: string[]): Observable<UpdateRecord[]> {
    console.log('getUpdateRecordsByTypeAndAreas__bySections__dcs')
    const areaString = areas.join(',');
    const params: { [key: string]: string } = { updateAreas: areaString };
    // params['isClosed'] = false.toString();
    // params['isMarkedForPreClosure'] = false.toString();
    return this.http.get<UpdateRecord[]>(`${this.apiUrl}/type/${type}/areas`, { params })
  }

  


  getUpdateRecordsByTypeAndAreas(
    type: string,
    areas: string[],
    isClosed?: boolean,
    isMarkedForClosure?: boolean,
    isMarkedForPreClosure?: boolean,
    forceUserScope: boolean = false,
    overrideSearchUserId?: number,
    isRoleNovedades_Operador_DCS: boolean = false
  ): Observable<UpdateRecord[]> {
    if(isRoleNovedades_Operador_DCS){
      //console.log(`SE ENTRA CON ROL Novedades_Operador_DCS`)
      return this.http.get<UpdateRecord[]>(`${this.apiUrl}/type/${type}/areas?updateAreas=general,cloruro_ferrico,salmuera_i,salmuera_ii,compresion,electrolizadores,subestacion,movimiento_producto,dcs`);

    }
    const areaString = areas.join(',');
    const params: { [key: string]: string } = { updateAreas: areaString };
    
    if (isClosed !== undefined) {
      params['isClosed'] = isClosed.toString();
    } 

    if (isMarkedForClosure !== undefined) {
      params['isMarkedForClosure'] = isMarkedForClosure.toString();
    }
    if (isMarkedForPreClosure !== undefined) {
      const isDcs = this.auth.hasRole('Novedades_Operador_DCS') || this.auth.hasRole('DCS') || this.auth.hasRole('dcs');
  const isFerrico = this.auth.hasRole('Novedades_Operador_Ferrico') || this.auth.hasRole('Novedades_Salmuera_I') || this.auth.hasRole('Novedades_Salmuera_II') || this.auth.hasRole('Novedades_Compresion') || this.auth.hasRole('Novedades_Electrolizadores') || this.auth.hasRole('Novedades_Subestacion') || this.auth.hasRole('Novedades_Movimiento_Producto') || this.auth.hasRole('FERRICO') || this.auth.hasRole('ferrico');
      const preParam = isDcs
        ? 'isMarkedForPreClosureDcs'
        : isFerrico
          ? 'isMarkedForPreClosureFerrico'
          : 'isMarkedForPreClosure';
      params[preParam] = isMarkedForPreClosure.toString();
    }
  
  const isFullNovedades = this.auth.hasRole('novedades') || this.auth.hasRole('Novedades_Operador_DCS');
    const baseUrl = `${this.apiUrl}/type/${encodeURIComponent(type)}/areas`;
    const userId = this.auth.getUserId();

    // Override explícito: siempre usar endpoint sin /user y forzar searchUserId
    if (overrideSearchUserId !== undefined) {
      params['searchUserId'] = String(overrideSearchUserId);
      return this.http.get<UpdateRecord[]>(baseUrl, { params })
        .pipe(map(list => this.normalizeClosureMark(list)));
    }

    const url = (!forceUserScope && isFullNovedades) ? baseUrl : `${baseUrl}/user/${userId}`;
    // Si usamos la URL sin /user/{id} y NO es 'novedades', agregar searchUserId como filtro adicional
    if (url === baseUrl && userId != null && !this.auth.hasRole('novedades')) {
      params['searchUserId'] = String(userId);
    }
    return this.http.get<UpdateRecord[]>(url, { params })
      .pipe(map(list => this.normalizeClosureMark(list)));
  }
  

  updateMarkForClosure(id: number, isMarked: boolean): Observable<void> {
    let role = 'jefeturno';
    if (this.auth.hasRole('Novedades_Operador_DCS')) {
      role = 'dcs';
    } else if (this.auth.hasRole('Novedades_Operador_Ferrico') || this.auth.hasRole('Novedades_Salmuera_I') || this.auth.hasRole('Novedades_Salmuera_II') || this.auth.hasRole('Novedades_Compresion') || this.auth.hasRole('Novedades_Electrolizadores') || this.auth.hasRole('Novedades_Subestacion') || this.auth.hasRole('Novedades_Movimiento_Producto')) {
      role = 'ferrico';
    }
    return this.http.patch<void>(`${this.apiUrl}/${id}/mark-for-closure?role=${role}`, isMarked);
  }  

  updateMarkForPreClosure(id: number, isMarked: boolean): Observable<void> {
    return this.http.patch<void>(`${this.apiUrl}/${id}/mark-for-pre-closure`, isMarked);
  }  

  updateMarkForClosureDaily(id: number, isMarked: boolean): Observable<void> {
    return this.http.patch<void>(`${this.apiUrl}/${id}/mark-for-closure-daily`, isMarked);
  }

  createUpdateRecord(updateRecord: CreateUpdateRecordDto): Observable<UpdateRecord> {
    const userId = this.auth.getUserId();
    const body = { ...updateRecord, userId } as any;
    return this.http.post<UpdateRecord>(this.apiUrl, body);
  }

  updateUpdateRecord(id: number, updateRecord: CreateUpdateRecordDto): Observable<UpdateRecord> {
  const userId = this.auth.getUserId();
  const body = { ...updateRecord, userId } as any;
  return this.http.patch<UpdateRecord>(`${this.apiUrl}/${id}`, body);
  }

  deleteUpdateRecord(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  cloneUpdateRecord(id: number): Observable<UpdateRecord> {
    return this.http.post<UpdateRecord>(`${this.apiUrl}/${id}/clone`, {});
  }

  

  getUpdateRecordsByShiftClosureAndArea(
    shiftClosureId: number,
    areas: string[],
    includeShiftClosureIdParam: boolean = false
  ): Observable<UpdateRecord[]> {
    const areaString = areas.join(',');
    let params = new HttpParams().set('updateAreas', areaString);
    const userId = this.auth.getUserId();
    if (userId != null) {
      params = params.set('searchUserId', String(userId));
    }
    if (includeShiftClosureIdParam) {
      params = params.set('shiftClosureId', String(shiftClosureId));
    }

    return this.http.get<UpdateRecord[]>(
      `${this.apiUrl}/shift-closure/${shiftClosureId}/area`,
      { params }
    ).pipe(map(list => this.normalizeClosureMark(list)));
  }

  getUpdateRecordsByShiftClosureBeforeAndArea(
    shiftClosureId: number,
    areas: string[]
  ): Observable<UpdateRecord[]> {
    const areaString = areas.join(',');
    let params = new HttpParams().set('updateAreas', areaString);
    
    return this.http.get<UpdateRecord[]>(
      `${this.apiUrl}/shift-closure-before/${shiftClosureId}/area`,
      { params }
    ).pipe(map(list => this.normalizeClosureMark(list)));
  }

  //nuevo
  getPreviousShiftClosure(filters: UpdateRecordSearchDTO): Observable<UpdateRecord[]> {
    const userId = this.auth.getUserId();
    return this.http.post<UpdateRecord[]>(`${this.apiUrl}/shift-closure-by-userId/${userId}`, filters);
  }

  getOpenShiftClosureInAreaByUser(): Observable<ShiftClosure> {
    const userId = this.auth.getUserId();
    return this.http.get<ShiftClosure>(`${this.apiUrl}/open-shift-closure-by-area/${userId}`)
  }

  getShiftClosureInSameArea(): Observable<ShiftClosure[]> {
    const userId = this.auth.getUserId();
    return this.http.get<ShiftClosure[]>(`${this.apiUrl}/shift-closures-in-area/${userId}`)
  }

}
